import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Select from '../../../../components/ui/Select';

const TeamFilters = ({
  selectedDateRange,
  selectedDepartment,
  selectedEmployees,
  selectedLocation,
  selectedStatusFilter,
  statusOptions,
  hasPendingCorrections,
  employees: employeesProp,
  departments: departmentsProp,
  locations: locationsProp,
  onFilterChange,
  onExport,
  exportDisabled,
  showNonWorkingDays,
  onShowNonWorkingDaysChange,
}) => {
  const { t } = useTranslation();
  
  const departments = Array.isArray(departmentsProp) && departmentsProp.length
    ? departmentsProp
    : [{ id: 'all', name: t('All Departments') }];

  const locations = Array.isArray(locationsProp) && locationsProp.length
    ? locationsProp
    : [{ id: 'all', name: t('All Locations') }];

  const statusFilters = [
    { id: 'all', name: t('All Statuses') },
    ...(hasPendingCorrections
      ? [{ id: 'correction_pending', name: t('Correction Pending') }]
      : []),
    ...((Array.isArray(statusOptions) ? statusOptions : [])
      .filter(Boolean)
      .map((s) => ({ id: `status:${String(s)}`, name: t(String(s)) })))
  ];

  const employees = employeesProp || [];

  const getEmployeeLabel = (u) => u?.fullName || u?.userName || u?.email || String(u?.id || '');

  const formatDateForInput = (date) => {
    return date?.toISOString()?.split('T')?.[0];
  };

  const handleStartDateChange = (e) => {
    let startDate = new Date(e.target.value);
    onFilterChange('dateRange', { start: startDate, end: selectedDateRange?.end });
  };

  const handleEndDateChange = (e) => {
    let endDate = new Date(e.target.value);
    onFilterChange('dateRange', { start: selectedDateRange?.start, end: endDate });
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    let startDate, endDate = today;

    switch (preset) {
      case 'today':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'yesterday':
        startDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        endDate = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'thisWeek':
        startDate = new Date(today.setDate(today.getDate() - today.getDay()));
        endDate = new Date();
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date();
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date();
    }

    onFilterChange('dateRange', { start: startDate, end: endDate });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      {/* Date Range Filters */}
      <div className="mb-6">
        <div className="flex flex-row lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-row sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{t('Date Range')}:</span>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={formatDateForInput(selectedDateRange?.start)}
                onChange={handleStartDateChange}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <span className="text-muted-foreground">{t('to')}</span>
              <input
                type="date"
                value={formatDateForInput(selectedDateRange?.end)}
                onChange={handleEndDateChange}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          {/* Quick Date Presets */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setPresetRange('today')}
              className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors"
            >
              {t('Today')}
            </button>
            <button
              onClick={() => setPresetRange('yesterday')}
              className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors"
            >
              {t('Yesterday')}
            </button>
            <button
              onClick={() => setPresetRange('thisWeek')}
              className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors"
            >
              {t('This Week')}
            </button>
            <button
              onClick={() => setPresetRange('thisMonth')}
              className="px-3 py-1.5 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-md transition-colors"
            >
              {t('This Month')}
            </button>
          </div>
        </div>
      </div>
      {/* Department, Location, and Correction Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Location Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{t('Location')}:</span>
          </div>
          <select
            value={selectedLocation}
            onChange={(e) => onFilterChange('location', e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {locations?.map((location) => (
              <option key={location?.id} value={location?.id}>
                {location?.name}
              </option>
            ))}
          </select>
        </div>

        {/* Department Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Building" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{t('Department')}:</span>
          </div>
          <select
            value={selectedDepartment}
            onChange={(e) => onFilterChange('department', e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {departments?.map((dept) => (
              <option key={dept?.id} value={dept?.id}>
                {dept?.name}
              </option>
            ))}
          </select>
        </div>

        {/* Employees Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{t('Employees')}:</span>
          </div>
          <Select
            multiple
            searchable
            options={employees.map(u => ({ label: getEmployeeLabel(u), value: u?.id }))}
            value={(selectedEmployees || []).map(u => u?.id)}
            onChange={(ids) => {
              const employeeMap = new Map(employees.map(u => [u?.id, u]));
              const selected = (ids || []).map(id => employeeMap.get(id)).filter(Boolean);
              onFilterChange('employees', selected);
            }}
            placeholder={t('Select employees')}
          />
        </div>

        {/* Status Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{t('Status')}:</span>
          </div>
          <select
            value={selectedStatusFilter}
            onChange={(e) => onFilterChange('status', e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {statusFilters?.map((filter) => (
              <option key={filter?.id} value={filter?.id}>
                {filter?.name}
              </option>
            ))
            }
          </select>
        </div>
      </div>
      {/* Show Non-Working Days toggle */}
      <div className="mt-4">
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showNonWorkingDays || false}
            onChange={(e) => onShowNonWorkingDaysChange?.(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          {t('Show non-working days')}
        </label>
      </div>
      {/* Applied Filters Summary */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex flex-row md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">{t('Applied filters')}:</span>
          
          {/* Date Range */}
          <div className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-xs">
            <Icon name="Calendar" size={12} />
            <span dir="ltr">
              {selectedDateRange?.start?.toLocaleDateString()} - {selectedDateRange?.end?.toLocaleDateString()}
            </span>
          </div>
          
          {/* Department */}
          {selectedDepartment !== 'all' && (
            <div className="flex items-center gap-1 bg-blue-500/10 text-blue-600 px-2 py-1 rounded-md text-xs">
              <Icon name="Building" size={12} />
              <span>
                {departments?.find(dept => dept?.id === selectedDepartment)?.name}
              </span>
            </div>
          )}

          {/* Employees */}
          {Array.isArray(selectedEmployees) && selectedEmployees.length > 0 && (
            <div className="flex items-center gap-1 bg-purple-500/10 text-purple-700 px-2 py-1 rounded-md text-xs">
              <Icon name="Users" size={12} />
              <span>{t('Employees')}: {selectedEmployees.length}</span>
            </div>
          )}
          
          {/* Location */}
          {selectedLocation !== 'all' && (
            <div className="flex items-center gap-1 bg-green-500/10 text-green-600 px-2 py-1 rounded-md text-xs">
              <Icon name="MapPin" size={12} />
              <span>
                {locations?.find(loc => loc?.id === selectedLocation)?.name}
              </span>
            </div>
          )}
          
          {/* Status */}
          {selectedStatusFilter !== 'all' && (
            <div className="flex items-center gap-1 bg-amber-500/10 text-amber-600 px-2 py-1 rounded-md text-xs">
              <Icon name="AlertCircle" size={12} />
              <span>
                {statusFilters?.find(filter => filter?.id === selectedStatusFilter)?.name}
              </span>
            </div>
          )}
          </div>

          {typeof onExport === 'function' ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!!exportDisabled}
                onClick={() => onExport('pdf')}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                <Icon name="FileText" size={16} />
                <span className="text-sm">{t('Export PDF')}</span>
              </button>
              <button
                type="button"
                disabled={!!exportDisabled}
                onClick={() => onExport('xlsx')}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                <Icon name="Sheet" size={16} />
                <span className="text-sm">{t('Export Excel')}</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TeamFilters;